import { Ref } from 'vue';

import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import { HttpStatus } from '@/common/constants';
import {
    showErrorNotificationFunction,
    showSuccessNotificationFunction,
} from '@/common/helpers';
import { projectModule } from '@/features/project/store';
import yup from '@/plugins/yup';

import { projectPlanningService } from '../services/planning.service';
import { projectPlanningModule } from '../store';
import { flattenTree } from '@/common/helperTree';
import { uniqBy } from 'lodash';
import { IResource } from '../interfaces';
import { sendMessageToIframe } from '../helper';

const assignResourceSchema = yup.object({
    tasks: yup.array().of(
        yup.object({
            resources: yup.array().of(
                yup.object({
                    resourceId: yup.mixed().required(),
                    appearanceProfileId: yup.mixed().required(),
                }),
            ),
        }),
    ),
});

export const innitAssignResource = {
    tasks: [],
};

export const setupAssignResource = () => {
    const { t } = useI18n();
    const { handleSubmit, errors, resetForm, setFieldValue, meta, validate } = useForm({
        initialValues: innitAssignResource,
        validationSchema: assignResourceSchema,
    });

    const onSubmit = handleSubmit(async (values) => {
        const response = await projectPlanningService.assignResourceBulk({
            projectId: projectModule.selectedProjectId || '',
            assignResource: (values.tasks || [])?.map((task: any) => ({
                taskId: task?.id || '',
                resourceCosts: task?.resourceCosts || [],
            })),
        });

        if (response.success) {
            showSuccessNotificationFunction(
                t('planning.assignResource.message.assignResourceSuccess'),
            );
            projectPlanningModule.setUpdatedTasks(response.data || []);
            projectPlanningModule.setIsShowAssignResourceForm(false);
        } else {
            if (response.code === HttpStatus.ITEM_NOT_FOUND) {
                projectPlanningModule.setIsShowAssignResourceForm(false);
                projectPlanningModule.setHasTaskDeleted(true);
            }
            showErrorNotificationFunction(response.message);
        }

        return response;
    });

    const { value: tasks } = useField('tasks') as { value: Ref<any[]> };

    const initFormValues = async () => {
        const getCustomIds = new Promise<string[]>((resolve) => {
            const timeout = setTimeout(() => {
                window.removeEventListener('message', process);
                resolve([]);
            }, 3000);

            const process = (e: MessageEvent) => {
                const data = e?.data;
                if (data?.type === 'current-selection') {
                    window.removeEventListener('message', process);
                    clearTimeout(timeout);
                    resolve(data?.data?.customIds || []);
                }
            };

            window.addEventListener('message', process);
        });

        sendMessageToIframe({
            action: 'current-selection',
        });

        const customIds = await getCustomIds;

        const selectedCustomObjectIds = customIds?.filter((id) => id);

        const flatedResources = flattenTree(projectPlanningModule.resourceTree);

        const selectedResources: IResource[] = [];
        const selectedResourcesParentIds: string[] = [];

        if (selectedCustomObjectIds?.length) {
            flatedResources?.forEach((resource) => {
                const customObjectIds = resource?.data?.customObjectIds?.filter(
                    (id: string) => id !== '#root',
                );

                const isSelected =
                    customObjectIds?.length &&
                    customObjectIds?.every((id: string) =>
                        selectedCustomObjectIds?.includes(id),
                    );

                if (isSelected) {
                    selectedResources.push(resource);
                    selectedResourcesParentIds.push(resource?.parentId);
                }
            });
        }

        const defaultResources =
            selectedResources
                ?.filter((resource) => {
                    return (
                        resource?._id &&
                        !selectedResourcesParentIds?.includes(resource?._id)
                    );
                })
                ?.map((resource, index) => {
                    return {
                        id: `${resource?._id}-${index}`,
                        resourceId: resource?._id || '',
                        appearanceProfileId: '',
                        viewer3dPathId: '',
                    };
                }) || [];

        const initValues = {
            tasks: projectPlanningModule.resourceAssignmentTreeForManyTasks?.map(
                (assignment) => {
                    const resources = Object.values(assignment)?.[0] || [];

                    const task = projectPlanningModule.planning?.tasks?.find(
                        (task) => task._id === Object.keys(assignment)?.[0],
                    );

                    let formResources = [];

                    if (resources?.length) {
                        const mappedResources = resources.map((resource: any, index) => ({
                            id: `${task?._id}-${index}`,
                            resourceId: resource?._id || '',
                            appearanceProfileId: resource?.appearanceProfileId || '',
                            viewer3dPathId: resource?.viewer3dPathId || '',
                        }));

                        if (defaultResources?.length) {
                            formResources = uniqBy(
                                mappedResources.concat(defaultResources),
                                'resourceId',
                            );
                        } else {
                            formResources = mappedResources;
                        }
                    } else {
                        if (defaultResources?.length) {
                            formResources = defaultResources;
                        } else {
                            formResources = [
                                {
                                    id: Date.now().toString(),
                                    resourceId: '',
                                    appearanceProfileId: '',
                                },
                            ];
                        }
                    }

                    return {
                        id: task?._id,
                        name: task?.name,
                        ganttId: task?.ganttId,
                        resources: formResources,
                    };
                },
            ),
        };

        resetForm({
            values: initValues,
        });

        return initValues;
    };

    const resetFormValues = () => {
        resetForm({
            values: innitAssignResource,
        });
    };

    return {
        meta,
        tasks,
        errors,
        onSubmit,
        setFieldValue,
        initFormValues,
        resetFormValues,
        validate,
        resetForm,
    };
};
